import * as Schema from "effect/Schema";
import { DesignSystemMentionList } from "./designSystemMention.ts";
import { TrimmedNonEmptyString } from "./baseSchemas.ts";
import {
  ApprovalRequestId,
  EventId,
  IsoDateTime,
  PositiveInt,
  ProviderItemId,
  ThreadId,
  TurnId,
} from "./baseSchemas.ts";
import {
  ChatAttachment,
  ModelSelection,
  PROVIDER_SEND_TURN_MAX_INPUT_CHARS,
  ProviderApprovalDecision,
  ProviderApprovalPolicy,
  ProviderInteractionMode,
  ProviderSkillInvocationList,
  ProviderRequestKind,
  ProviderSandboxMode,
  RuntimeMode,
} from "./orchestration.ts";
import {
  ProviderInstanceId,
  ProviderDriverKind,
  ProviderInstanceOwnership,
} from "./providerInstance.ts";
import { ProviderUserInputAnswers } from "./providerUserInput.ts";

const ProviderSessionStatus = Schema.Literals([
  "connecting",
  "ready",
  "running",
  "error",
  "closed",
]);

/** Durable authority describing how an AFAR thread acquired its native conversation. */
export const ProviderConversationOrigin = Schema.Literals(["fresh", "resumed", "forked"]);
export type ProviderConversationOrigin = typeof ProviderConversationOrigin.Type;

const ContentDigest = Schema.String.check(Schema.isPattern(/^[0-9a-f]{64}$/));

export const GeneralChatQueryAvailability = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("available"),
    brainRoot: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    type: Schema.Literal("unavailable"),
    reason: Schema.Literal("brain-layout-missing"),
    mutating: Schema.Literal(false),
    message: TrimmedNonEmptyString,
  }),
]);
export type GeneralChatQueryAvailability = typeof GeneralChatQueryAvailability.Type;

const SkillBundleIdentity = Schema.Struct({
  bundleId: TrimmedNonEmptyString,
  packageDigest: ContentDigest,
  inventoryDigest: ContentDigest,
});

export const ProviderRuntimeIdentity = Schema.Struct({
  runtime: TrimmedNonEmptyString,
  version: Schema.optional(TrimmedNonEmptyString),
  nativeVersion: Schema.optional(TrimmedNonEmptyString),
});
export type ProviderRuntimeIdentity = typeof ProviderRuntimeIdentity.Type;

/** Non-secret evidence identifying the runtime/provenance observed at bind time. */
export const ProviderRuntimeProvenanceIdentity = Schema.Struct({
  kind: Schema.Literals(["verified-installation", "official-artifact", "observed-runtime"]),
  identity: TrimmedNonEmptyString,
});
export type ProviderRuntimeProvenanceIdentity = typeof ProviderRuntimeProvenanceIdentity.Type;

/** Immutable Provider Instance identity retained after its catalog row is deleted. */
export const ProviderBindingIdentity = Schema.Struct({
  ownership: ProviderInstanceOwnership,
  configurationRevision: ContentDigest,
  runtimeProvenance: ProviderRuntimeProvenanceIdentity,
});
export type ProviderBindingIdentity = typeof ProviderBindingIdentity.Type;

/** Immutable identities applied to one persistent provider thread. */
export const ThreadContextBinding = Schema.Struct({
  revision: PositiveInt,
  applicationRelease: TrimmedNonEmptyString,
  source: Schema.Struct({
    packageName: TrimmedNonEmptyString,
    treeDigest: ContentDigest,
  }),
  workspaceInstructionProfile: Schema.Struct({
    profileId: TrimmedNonEmptyString,
    contentDigest: ContentDigest,
    inventoryDigest: ContentDigest,
  }),
  /** Discoverable Skill Bundle inventory for this Thread Scope. */
  skillInventory: Schema.Array(SkillBundleIdentity),
  /** Repository-relative Feature Workspace Working Directory for this Thread Scope. */
  featureWorkspaceWorkingDirectory: TrimmedNonEmptyString,
  providerAdapter: Schema.Struct({
    driver: ProviderDriverKind,
    instanceId: ProviderInstanceId,
  }),
  providerRuntime: ProviderRuntimeIdentity,
  // Optional on decode so historical bindings remain readable. Every new
  // workspace binding records this evidence before execution begins.
  providerIdentity: Schema.optional(ProviderBindingIdentity),
  queryAvailability: GeneralChatQueryAvailability,
});
export type ThreadContextBinding = typeof ThreadContextBinding.Type;

export const ThreadContextTransitionEvidence = Schema.Struct({
  fromRevision: PositiveInt,
  toRevision: PositiveInt,
});
export type ThreadContextTransitionEvidence = typeof ThreadContextTransitionEvidence.Type;

export const ProviderSession = Schema.Struct({
  provider: ProviderDriverKind,
  // Optional during the driver/instance migration. Once every producer
  // populates it (post-slice-4), routing flips to instance-id-only and the
  // legacy `provider` field is removed.
  providerInstanceId: Schema.optional(ProviderInstanceId),
  status: ProviderSessionStatus,
  runtimeMode: RuntimeMode,
  cwd: Schema.optional(TrimmedNonEmptyString),
  model: Schema.optional(TrimmedNonEmptyString),
  threadId: ThreadId,
  conversationOrigin: Schema.optional(ProviderConversationOrigin),
  /**
   * Whether *this* start handed the provider a brand-new native conversation
   * instead of picking the thread's persisted one back up.
   *
   * `conversationOrigin` cannot answer this: it is the conversation's durable
   * provenance, so a thread born fresh reports "fresh" for the rest of its
   * life, including on a cold start that resumed perfectly. This field
   * describes the single attachment that produced this session and is not
   * persisted with the binding — read it from the session a start returned,
   * not from one that was listed or recovered later.
   */
  openedFreshConversation: Schema.optional(Schema.Boolean),
  resumeCursor: Schema.optional(Schema.Unknown),
  activeTurnId: Schema.optional(TurnId),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  lastError: Schema.optional(TrimmedNonEmptyString),
});
export type ProviderSession = typeof ProviderSession.Type;

/** JSON scalar retained as typed reference data until a provider adapter encodes it. */
export const ProviderStructuredReferenceScalar = Schema.Union([
  Schema.String,
  Schema.Finite,
  Schema.Boolean,
  Schema.Null,
]);
export type ProviderStructuredReferenceScalar = typeof ProviderStructuredReferenceScalar.Type;

/** Provider-neutral reference data. Entry order is semantically significant. */
export const ProviderStructuredReference = Schema.Struct({
  type: TrimmedNonEmptyString,
  attributes: Schema.Record(TrimmedNonEmptyString, ProviderStructuredReferenceScalar),
});
export type ProviderStructuredReference = typeof ProviderStructuredReference.Type;

/** Versioned envelope for optional privileged structured-reference data. */
export const ProviderStructuredReferencePayload = Schema.Struct({
  entries: Schema.Array(ProviderStructuredReference),
});
export type ProviderStructuredReferencePayload = typeof ProviderStructuredReferencePayload.Type;

/** Ephemeral provider-neutral context supplied through a native privileged boundary. */
export const ProviderPrivilegedContext = Schema.Struct({
  developerInstructions: TrimmedNonEmptyString,
  structuredReferences: Schema.optional(ProviderStructuredReferencePayload),
});
export type ProviderPrivilegedContext = typeof ProviderPrivilegedContext.Type;

export const ProviderSessionStartInput = Schema.Struct({
  threadId: ThreadId,
  provider: Schema.optional(ProviderDriverKind),
  // See ProviderSession for the migration story.
  providerInstanceId: Schema.optional(ProviderInstanceId),
  cwd: Schema.optional(TrimmedNonEmptyString),
  modelSelection: Schema.optional(ModelSelection),
  resumeCursor: Schema.optional(Schema.Unknown),
  forkCursor: Schema.optional(Schema.Unknown),
  approvalPolicy: Schema.optional(ProviderApprovalPolicy),
  sandboxMode: Schema.optional(ProviderSandboxMode),
  runtimeMode: RuntimeMode,
  contextBinding: Schema.optional(ThreadContextBinding),
  contextTransition: Schema.optional(ThreadContextTransitionEvidence),
  privilegedContext: Schema.optional(ProviderPrivilegedContext),
  continuationRequirement: Schema.optional(Schema.Literal("required")),
});
export type ProviderSessionStartInput = typeof ProviderSessionStartInput.Type;

export const ProviderSendTurnInput = Schema.Struct({
  threadId: ThreadId,
  /** Internal recovery signal. Allows an empty turn only for adapters that
      explicitly support promptless continuation. */
  continuation: Schema.optional(Schema.Boolean),
  input: Schema.optional(
    TrimmedNonEmptyString.check(Schema.isMaxLength(PROVIDER_SEND_TURN_MAX_INPUT_CHARS)),
  ),
  attachments: Schema.optional(Schema.Array(ChatAttachment)),
  designSystemMentions: DesignSystemMentionList,
  skillInvocations: Schema.optional(ProviderSkillInvocationList),
  modelSelection: Schema.optional(ModelSelection),
  interactionMode: Schema.optional(ProviderInteractionMode),
  privilegedContext: Schema.optional(ProviderPrivilegedContext),
});
export type ProviderSendTurnInput = typeof ProviderSendTurnInput.Type;

export const ProviderSteerTurnInput = Schema.Struct({
  threadId: ThreadId,
  turnId: TurnId,
  input: Schema.optional(
    TrimmedNonEmptyString.check(Schema.isMaxLength(PROVIDER_SEND_TURN_MAX_INPUT_CHARS)),
  ),
  attachments: Schema.optional(Schema.Array(ChatAttachment)),
  designSystemMentions: DesignSystemMentionList,
  skillInvocations: Schema.optional(ProviderSkillInvocationList),
});
export type ProviderSteerTurnInput = typeof ProviderSteerTurnInput.Type;

export const ProviderTurnStartResult = Schema.Struct({
  threadId: ThreadId,
  turnId: TurnId,
  resumeCursor: Schema.optional(Schema.Unknown),
});
export type ProviderTurnStartResult = typeof ProviderTurnStartResult.Type;

export const ProviderUploadFeedbackInput = Schema.Struct({
  threadId: ThreadId,
  reason: Schema.optional(TrimmedNonEmptyString),
});
export type ProviderUploadFeedbackInput = typeof ProviderUploadFeedbackInput.Type;

export const ProviderUploadFeedbackResult = Schema.Struct({
  feedbackId: TrimmedNonEmptyString,
});
export type ProviderUploadFeedbackResult = typeof ProviderUploadFeedbackResult.Type;

export class ProviderUploadFeedbackError extends Schema.TaggedErrorClass<ProviderUploadFeedbackError>()(
  "ProviderUploadFeedbackError",
  {
    threadId: ThreadId,
    cause: Schema.optional(Schema.Defect()),
  },
) {
  override get message(): string {
    return `Failed to upload feedback for thread ${this.threadId}.`;
  }
}

export const ProviderInterruptTurnInput = Schema.Struct({
  threadId: ThreadId,
  turnId: Schema.optional(TurnId),
});
export type ProviderInterruptTurnInput = typeof ProviderInterruptTurnInput.Type;

export const ProviderStopSessionInput = Schema.Struct({
  threadId: ThreadId,
});
export type ProviderStopSessionInput = typeof ProviderStopSessionInput.Type;

export const ProviderRespondToRequestInput = Schema.Struct({
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  decision: ProviderApprovalDecision,
});
export type ProviderRespondToRequestInput = typeof ProviderRespondToRequestInput.Type;

export const ProviderRespondToUserInputInput = Schema.Struct({
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  answers: ProviderUserInputAnswers,
});
export type ProviderRespondToUserInputInput = typeof ProviderRespondToUserInputInput.Type;

const ProviderEventKind = Schema.Literals(["session", "notification", "request", "error"]);

export const ProviderEvent = Schema.Struct({
  id: EventId,
  kind: ProviderEventKind,
  provider: ProviderDriverKind,
  // See ProviderSession for the migration story.
  providerInstanceId: Schema.optional(ProviderInstanceId),
  threadId: ThreadId,
  createdAt: IsoDateTime,
  method: TrimmedNonEmptyString,
  message: Schema.optional(TrimmedNonEmptyString),
  turnId: Schema.optional(TurnId),
  itemId: Schema.optional(ProviderItemId),
  requestId: Schema.optional(ApprovalRequestId),
  requestKind: Schema.optional(ProviderRequestKind),
  /** Native child conversation that produced an event routed through its parent turn. */
  childThreadId: Schema.optional(TrimmedNonEmptyString),
  textDelta: Schema.optional(Schema.String),
  payload: Schema.optional(Schema.Unknown),
});
export type ProviderEvent = typeof ProviderEvent.Type;
